import {
  TacticalBoardThumbnail,
  tacticalBoardHasContent,
} from "@/features/canvas/TacticalBoardThumbnail";

/**
 * Read-only chip-sized tactical-board preview for an observation
 * note's thumbnail slot. Ports the legacy `coach-thumb--board`
 * chip variant (`js/coaching/feedback.js:299`) +
 * `tacticalBoardHasContent` gate.
 *
 * Renders only when the raw `tactical_board_json` parses cleanly
 * AND has at least one token or shape. Returns `null` otherwise —
 * the caller falls back to the generic observation glyph. This
 * matches the legacy `hasBoard = isObservation &&
 * tacticalBoardHasContent(...)` ternary.
 *
 * Sized to the 80×48 px tile the rest of the viewer thumbs use
 * (`h-12 w-20`); `TacticalBoardView` derives the height from the
 * pitch aspect so we pass width=80 and let the pitch settle into
 * the tile. Wrapped in a fixed-size box with `overflow-hidden` so
 * a slight aspect mismatch stays clipped to the tile.
 */

export type BoardChipProps = {
  /** Raw `tactical_board_json` from a `CoachingNote`. */
  raw: unknown;
};

export function BoardChip({ raw }: BoardChipProps) {
  if (!boardHasContent(raw)) return null;

  return (
    <TacticalBoardThumbnail
      raw={raw}
      width={80}
      fallback="none"
      testId="feedback-board-chip"
      className="block h-12 w-20 shrink-0 overflow-hidden rounded border border-rule/60 bg-surface"
    />
  );
}

/** True if the raw `tactical_board_json` has at least one token
 * or shape. Useful when the caller needs to decide BEFORE
 * mounting the chip (e.g. to render a "⌬ Board" indicator pill
 * alongside the thumbnail). */
export function boardHasContent(raw: unknown): boolean {
  return tacticalBoardHasContent(raw);
}
